import React from 'react';
import Layout from '../components/Layout';
import { HiCodeBracket } from 'react-icons/hi2';

const CommandPage = () => {
    const commands = [
        {
            title: "Add Member TGID",
            command: "/addid [godid] [tgid]",
            example: "/addid 123 456789",
            description: "Links a member's godID to their Telegram ID",
            response: [
                "New member: \"New member spa123 is created successfully with TGID: 456789\"",
                "Existing member: \"godMember's fullName [name] is tagged to 456789\""
            ],
            bgColor: "bg-blue-50"
        },
        {
            title: "Add Monitor Group",
            commands: [
                {
                    cmd: "/addmonitorid [groupid]",
                    example: "/addmonitorid -100123456789",
                    response: "[Group Title] is monitor now."
                },
                {
                    cmd: "/addmonitorusername [@username]",
                    example: "/addmonitorusername @groupname",
                    response: "@groupname is monitor now."
                }
            ],
            description: "Adds a group to monitoring list",
            bgColor: "bg-green-50"
        },
        {
            title: "Check Member Posts",
            command: "/checkpost [godid]",
            example: "/checkpost 123",
            description: "Shows the last 5 days of posting history for all Telegram accounts linked to the member",
            response: [
                "Shows member's name, total accounts, and detailed post history per account including:",
                "- Account details (TGID, username)",
                "- Post counts per group",
                "- Dates of activity"
            ],
            bgColor: "bg-yellow-50"
        },
        {
            title: "View Member Details",
            command: "/details [godid]",
            example: "/details 123",
            description: "Shows comprehensive information about a member",
            response: [
                "Displays detailed member information including:",
                "- Renewal Date",
                "- Remarks",
                "- Name",
                "- Nationality",
                "- Contact Number",
                "- Telegram Accounts",
                "- Payment History (Last payment and total payments)",
                "- God Group Information",
                "- Shop Assignment (if any)"
            ],
            bgColor: "bg-pink-50"
        },
        {
            title: "Search by Phone Number",
            command: "/searchphone [phonenumber]",
            example: "/searchphone 6581818181",
            description: "Searches for a member using their current or old phone number",
            response: [
                "Shows member details (same format as /details) if found, including:",
                "- Current phone number(s)",
                "- Old phone number(s) if any",
                "- All other member information",
                "Found Message: \"Found Member with phone number: [number]\"",
                "Not Found: \"No member found with phone number: [number]\""
            ],
            bgColor: "bg-purple-50"
        },
        {
            title: "Get Latest Index",
            command: "/getlatestindex",
            example: "/getlatestindex",
            description: "Returns the highest numeric value found in all godIDs",
            response: [
                "\"Latest godID index: [number]\"",
                "- Works with pure numbers (e.g., \"334\")",
                "- Works with mixed formats (e.g., \"spa334\", \"334test\")",
                "- Returns \"No numeric godIDs found\" if no numbers found"
            ],
            bgColor: "bg-orange-50"
        },
        {
            title: "Create God Group",
            command: "/creategroup [groupname] [handler_godid] [participant_godids] [date]",
            example: "/creategroup 122G1 122 122,603,604 300525",
            description: "Creates a new god group with specified handler and participants",
            response: [
                "Shows created group details including:",
                "- Group name",
                "- Handler name/ID",
                "- List of participants with join dates",
                "- Error if group name exists with suggested alternative",
                "Note: date parameter is optional (ddmmyy format)"
            ],
            bgColor: "bg-indigo-50"
        },
        {
            title: "Add Payment",
            commands: [
                {
                    cmd: "/addindpay [godid] [amount] [paymentdate] [renewaldate] [\"remarks\"]",
                    example: "/addindpay 122 700 300525 300625 \"first payment\"",
                    description: "Creates individual payment for a member"
                },
                {
                    cmd: "/addgrouppay [groupname] [amount] [paymentdate] [renewaldate] [\"remarks\"]",
                    example: "/addgrouppay 122G1 1400 300525 300625 \"group payment\"",
                    description: "Creates group payment for a god group"
                },
                {
                    cmd: "/addaddpay [groupname] [amount] [paymentdate] [renewaldate] [\"remarks\"]",
                    example: "/addaddpay 122G1 800 300525 300625 \"add-on payment\"",
                    description: "Creates add-on payment for a god group"
                }
            ],
            response: [
                "Shows payment details including:",
                "- Payment type (individual/group/add-on)",
                "- Amount",
                "- Payment and renewal dates",
                "- Member/Group details",
                "- Remarks (if provided)",
                "Note: remarks in quotes are optional"
            ],
            bgColor: "bg-red-50"
        }
    ];

    return (
        <Layout>
            <div className="px-6 py-8">
                <div className="flex items-center mb-8">
                    <HiCodeBracket className="w-8 h-8 mr-3 text-indigo-600" />
                    <h1 className="text-3xl font-bold">Bot Commands</h1>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {commands.map((cmd, index) => (
                        <div key={index} className={`rounded-lg shadow-md p-6 ${cmd.bgColor} hover:shadow-lg transition-shadow duration-200`}>
                            <h2 className="text-xl font-semibold mb-4">{cmd.title}</h2>
                            
                            {cmd.commands ? (
                                // For commands with multiple variations (like Add Monitor Group)
                                <div className="space-y-4">
                                    {cmd.commands.map((variation, idx) => (
                                        <div key={idx}>
                                            <p className="text-purple-600 font-mono mb-2">{variation.cmd}</p>
                                            <p className="text-blue-600 font-mono mb-2">{variation.example}</p>
                                            {variation.description && (
                                                <p className="text-gray-700 mb-2">{variation.description}</p>
                                            )}
                                            {variation.response && (
                                                <p className="text-gray-600 italic">{variation.response}</p>
                                            )}
                                        </div>
                                    ))}
                                    {cmd.response && (
                                        <div className="mt-4 space-y-1">
                                            {Array.isArray(cmd.response) ? (
                                                cmd.response.map((res, idx) => (
                                                    <p key={idx} className="text-gray-600 italic">{res}</p>
                                                ))
                                            ) : (
                                                <p className="text-gray-600 italic">{cmd.response}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                // For single commands
                                <div>
                                    <p className="text-purple-600 font-mono mb-2">{cmd.command}</p>
                                    <p className="text-blue-600 font-mono mb-2">{cmd.example}</p>
                                    <p className="text-gray-700 mb-4">{cmd.description}</p>
                                    <div className="space-y-1">
                                        {Array.isArray(cmd.response) ? (
                                            cmd.response.map((res, idx) => (
                                                <p key={idx} className="text-gray-600 italic">{res}</p>
                                            ))
                                        ) : (
                                            <p className="text-gray-600 italic">{cmd.response}</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </Layout>
    );
};

export default CommandPage; 